import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';

const CuetDashboard = () => {
    const [mockUser, setMockUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // 1. Load User Data on Mount
    useEffect(() => {
        const loadUser = async () => {
            const storedUser = localStorage.getItem('mockCuetUser');
            if (!storedUser) {
                navigate('/services/counselling/login');
                return;
            }

            const parsedUser = JSON.parse(storedUser);
            
            // Fetch fresh data from DB to ensure step sync
            try {
                const res = await api.get(`/mock-cuet/application-data?mockAppId=${parsedUser.id}`);
                if (res.data.success) {
                    setMockUser({
                        ...parsedUser,
                        currentStep: res.data.data.currentStep,
                        stepStatus: res.data.data.stepStatus,
                        formData: res.data.data.formData,
                        // Ensure name is synced if changed
                        candidateName: res.data.data.candidate_name || parsedUser.candidateName 
                    });
                }
            } catch (err) {
                console.error("Error refreshing user data", err);
                // Fallback to local storage if API fails
                setMockUser(parsedUser);
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, [navigate]);

    // 2. Helper to refresh state after a step is saved
    const refreshUser = (nextStep) => {
        setMockUser(prev => ({
            ...prev,
            currentStep: Math.max(prev.currentStep, nextStep)
        }));
        
        // Update Local Storage
        const stored = JSON.parse(localStorage.getItem('mockCuetUser'));
        if(stored) {
            stored.currentStep = Math.max(stored.currentStep, nextStep);
            localStorage.setItem('mockCuetUser', JSON.stringify(stored));
        }
    };

    // 3. Define Steps
    const steps = [
        { id: 1, name: "Contact Details", path: "contact-details" },
        { id: 2, name: "Personal Details", path: "personal-details" },
        { id: 3, name: "Disability Details", path: "pwd-details" },
        { id: 4, name: "Identity Details", path: "identity-details" },
        { id: 5, name: "APAAR ID", path: "apaar-id" },
        { id: 6, name: "Apply For Exam", path: "exam-center" },
        { id: 7, name: "Qualification Details", path: "qualification-details" },
        { id: 8, name: "Additional Details", path: "additional-details" },
        { id: 9, name: "Emergency Contact", path: "emergency-contact" },
        { id: 10, name: "Capture Photograph", path: "photo-capture" },
        { id: 11, name: "Upload Documents", path: "upload-documents" },
        { id: 12, name: "Final Submit", path: "final-submit" },
    ];

    // 4. Navigation Handler
    const handleStepClick = (step) => {
        if (step.id <= mockUser.currentStep) {
            navigate(step.path);
        } else {
            alert("Please complete the previous steps first.");
        }
    };

    if (loading || !mockUser) return <div className="text-center p-5">Loading Application System...</div>;

    // 5. Logic to decide which Sidebar to show
    // If URL ends in /dashboard or /dashboard/, show Activities. Else show Steps.
    const isHomePage = location.pathname.endsWith('/dashboard') || location.pathname.endsWith('/dashboard/');

    const renderSidebar = () => {
        if (isHomePage) {
            // --- HOME PAGE SIDEBAR (Activities) ---
            return (
                <div>
                <div className="panel panel-default" style={{ borderRadius: 0 }}>
                    <div className="panel-heading" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        Activities
                    </div>
                    <div className="list-group">
                        <button className="list-group-item list-group-item-success" style={{textAlign: 'left', cursor:'default'}}>
                            <i className="fa fa-check-circle" style={{ marginRight: '10px' }}></i> Registration Form
                        </button>
                        <button 
                            className="list-group-item" 
                            onClick={() => navigate('contact-details')}
                            style={{textAlign: 'left', color: '#337ab7'}}
                        >
                             <i className="fa fa-clock-o" style={{ marginRight: '10px' }}></i> Application Form
                        </button>
                        <button className="list-group-item" style={{textAlign: 'left', cursor:'not-allowed', color: '#999'}}>
                             <i className="fa fa-clock-o" style={{ marginRight: '10px' }}></i> Fee Payment
                        </button>
                    </div>
                </div>
                {/* Verify Mobile/Email Panel (Visual Only for Mock) */}
                <div className="panel panel-default" style={{marginTop:'30px'}}>
                    <div className="panel-heading" style={{fontWeight:'bold'}}>Verify Mobile No. & Email Id</div>
                    <div className="panel-body">
                        <ul className="list-unstyled">
                            <li style={{color:'green', marginBottom:'5px'}}>
                                <i className="fa fa-check-circle"></i> Mobile No.- Verified
                            </li>
                            <li style={{color:'orange'}}>
                                <i className="fa fa-clock-o"></i> Verify Email Id
                            </li>
                        </ul>
                    </div>
                </div>
                </div>
            );
        } else {
            // --- STEPS SIDEBAR (When inside a form step) ---
            return (
                <div className="panel panel-default" style={{ borderRadius: 0 }}>
                    <div className="panel-heading" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        Application Form Steps
                    </div>
                    <div className="list-group">
                        {steps.map((step) => {
                            const isCompleted = step.id < mockUser.currentStep;
                            const isActive = location.pathname.includes(step.path);
                            const isLocked = step.id > mockUser.currentStep;

                            let itemClass = "list-group-item";
                            if (isActive) itemClass += " active";
                            else if (isCompleted) itemClass += " list-group-item-success";
                            else if (isLocked) itemClass += " disabled";

                            return (
                                <button
                                    key={step.id}
                                    type="button"
                                    className={itemClass}
                                    onClick={() => handleStepClick(step)}
                                    style={{ textAlign: 'left', cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                >
                                    {isCompleted ? <i className="fa fa-check-circle text-success" style={{ marginRight: '10px' }}></i> : 
                                     isActive ? <i className="fa fa-arrow-circle-right" style={{ marginRight: '10px' }}></i> :
                                     <i className="fa fa-circle-o" style={{ marginRight: '10px' }}></i>}
                                    {step.id}. {step.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="container-fluid contentmid" style={{ marginTop: '20px' }}>
            <div className="row">
                
                {/* --- SIDEBAR --- */}
                <div className="col-sm-3">
                    {renderSidebar()}
                    
                    {/* User Info Box */}
                    <div className="alert alert-info text-center" style={{marginTop:'20px'}}>
                        <strong>Logged in as:</strong><br/>
                        {/* Fix: Use candidateName from login response/DB */}
                        {mockUser.candidateName}<br/>
                        <small>App No: {mockUser.applicationNo}</small>
                    </div>
                </div>

                {/* --- MAIN CONTENT AREA --- */}
                <div className="col-sm-9">
                    {/* Pass context to child steps */}
                    <Outlet context={{ mockUser, refreshUser }} />
                </div>
            </div>
        </div>
    );
};

export default CuetDashboard;